import React, { useCallback, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import useBedbankPackageViews from 'hooks/useBedbankPackageViews'
import { OfferPageActions, OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import uuidV4 from 'lib/string/uuidV4Utils'
import useQueryParams from 'hooks/useQueryParams'
import BedbankRoomOptionRate from '../../BedbankRoomOptionRate'
import { sortBy } from 'lib/array/arrayUtils'
import { GOOGLE_ADS_UTM_CONTENT } from 'constants/bedbank'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import LineExclamationCircleIcon from 'LuxKit/Icons/line/LineExclamationCircleIcon'
import TextLink from 'LuxKit/TextLink'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { useAppSelector } from 'hooks/reduxHooks'
import { getSource } from 'selectors/offerSelectors'
import BodyText from 'LuxKit/Typography/BodyText'
import BedbankMobileStickyHeaderFilters from '../BedbankMobileStickyHeaderFilters'

const RoomHeaderContainer = styled(Group)`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  offer: App.BedbankOffer;
  selectedBedbankRoomId: string;
  onClose: () => void;
  onBack?: () => void;
}

function BedbankPackageSelectionModal(props: Props) {
  const {
    selectedBedbankRoomId,
    offer,
    onClose,
    onBack,
  } = props

  const offerPageDispatch = useOfferPageDispatch()
  const query = useQueryParams()
  const source = useAppSelector((state) => getSource(state))
  const roomTypeId = query.get('roomTypeId')
  const utmContent = query.get('utm_content')

  const [packageViews, fetchingRates] = useBedbankPackageViews(offer)
  const selectedPackageView = useMemo(() => packageViews.find(view => view.package.roomId === selectedBedbankRoomId), [packageViews, selectedBedbankRoomId])

  const showAll = packageViews.filter(p => p.available).length === 0
  const sortedPackageViews = useMemo(() => {
    return sortBy(packageViews, pkg => {
      if (pkg.cheapestRate) {
        return pkg.cheapestRate.totals.inclusive + (pkg.cheapestRate.totals.propertyFees ?? 0)
      }

      if (!pkg.capacityValid) {
        return Number.MAX_SAFE_INTEGER - 1
      }

      return Number.MAX_SAFE_INTEGER
    }, 'asc')
  }, [packageViews])

  const sortedPackages = useMemo(() => {
    const highlightedPackage = sortedPackageViews.find(p => p.package.roomId === roomTypeId)
    if (utmContent === GOOGLE_ADS_UTM_CONTENT && highlightedPackage) {
      return [
        highlightedPackage,
        sortedPackageViews.find(p => p.package.roomId !== roomTypeId),
      ].filter((p): p is App.BedbankOfferPackageView => !!p) // this fix type inference
    }
    else {
      return sortedPackageViews
    }
  }, [sortedPackageViews, utmContent, roomTypeId])

  const displayablePackages = useMemo(() => sortedPackages.filter(
    packageView => (packageView.show && packageView.available) || (packageView.show && showAll),
  ), [sortedPackages, showAll])

  const roomPosition = useMemo(() => displayablePackages.findIndex(p => p.package.roomId === selectedPackageView?.package.roomId), [displayablePackages, selectedPackageView])

  const roomListId = useMemo(() => uuidV4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPackageView])

  const rateListId = useMemo(() => uuidV4(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedPackageView?.rates])

  const handleCheckAvailabilityClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: selectedPackageView?.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
  }, [offerPageDispatch, selectedPackageView?.package.roomId])

  const handleChangeDatesClick = useCallback(() => {
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: selectedPackageView?.package.roomId })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
  }, [offerPageDispatch, selectedPackageView?.package.roomId])

  return (
    <>
      <ModalHeader
        title="Select a package"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <BedbankMobileStickyHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <Pane background="neutral-six">
          <LoadingIndicator visible={fetchingRates} floating opaque />
          <Group direction="vertical" gap={8}>
            {selectedPackageView && (
              <RoomHeaderContainer direction="vertical" gap={20} noDisplayWhenEmpty>
                {selectedPackageView.rates.length > 1 && (
                  <BodyText variant="small" colour="neutral-three">{pluralizeToString('package option', selectedPackageView.rates.length)} available</BodyText>
                )}
                {!fetchingRates && !selectedPackageView.rates.length && (!selectedPackageView?.available || !selectedPackageView?.capacityValid) && (
                  <Group direction="vertical" gap={8}>
                    {!selectedPackageView?.capacityValid && <MessageBanner
                      kind="informational-primary"
                      icon={<LineExclamationCircleIcon />}
                      heading="This room cannot accomodate your number of guests"
                      description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleCheckAvailabilityClick}>add more rooms</TextLink></>}
                    />}
                    {(!selectedPackageView?.available || !selectedPackageView?.show) && <MessageBanner
                      kind="informational-primary"
                      icon={<LineExclamationCircleIcon />}
                      heading="This room is sold out on your dates or max number of guests exceeded"
                      description={<>Choose another room or <TextLink size="medium" weight="regular" onClick={handleChangeDatesClick}>try different dates</TextLink></>}
                    />}
                  </Group>
                )}
              </RoomHeaderContainer>
            )}
            {selectedPackageView?.rates.map((rate, index) => <Pane key={rate.id}>
              <BedbankRoomOptionRate
                key={rate.id}
                rate={rate}
                offer={offer}
                roomListId={roomListId}
                packageView={selectedPackageView}
                optionNumber={index + 1}
                rateListId={rateListId}
                roomPosition={Math.max(roomPosition, 0) + 1}
                source={source}
                showOptionLabel={selectedPackageView.rates.length > 1}
              />
            </Pane>)}
          </Group>
        </Pane>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState) => ({
  selectedBedbankRoomId: state.selectedBedbankRoomId,
  offer: state.bedbankOffer!,
}), BedbankPackageSelectionModal)
